import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function LoginPage() {
  const [cred, setCred] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!cred.username.trim() || !cred.password) {
      toast.error("Username dan password wajib diisi");
      return;
    }
    setLoading(true);
    try {
      const data = await loginUser(cred.username.trim(), cred.password);
      toast.success(`Selamat datang, ${data.label}`);
      navigate(`/${data.role}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen relative flex flex-col items-center justify-center px-5 py-12"
      data-testid="login-page"
    >
      <div className="absolute inset-0 grid-lines opacity-50" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-blue-700/15 blur-[150px]" />

      <Link
        to="/"
        data-testid="back-home-link"
        className="absolute top-6 left-5 md:left-12 z-10 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-blue-400 transition-colors duration-300"
      >
        <ArrowLeft className="w-4 h-4" /> Kembali
      </Link>

      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-10">
          <Logo size={64} />
        </div>

        <form onSubmit={submit} className="glass rounded-3xl p-7 md:p-10 fade-up" data-testid="user-login-panel">
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.22em] text-blue-400 font-semibold">
              Masuk Sistem Job Order
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold mt-3">
              Login <span className="metal-text">Akun</span>
            </h1>
            <p className="text-sm text-slate-400 mt-3">
              Gunakan username dan password yang dibuat oleh Kumendan Pasukan.
            </p>
          </div>

          <div className="mt-8 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">Username</Label>
              <Input
                data-testid="login-username-input"
                autoComplete="username"
                value={cred.username}
                onChange={(e) => setCred({ ...cred, username: e.target.value })}
                className="mt-2 h-12 bg-slate-900/70 border-white/10"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                value={cred.password}
                onChange={(e) => setCred({ ...cred, password: e.target.value })}
                className="mt-2 h-12 bg-slate-900/70 border-white/10"
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="user-login-submit"
            disabled={loading}
            className="w-full mt-8 h-12 rounded-xl bg-blue-600 hover:bg-blue-500 font-semibold"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <><LogIn className="w-4 h-4 mr-2" /> Masuk</>
            )}
          </Button>

          <p className="mt-6 text-center text-xs text-slate-500">
            Lupa akun? Hubungi Kumendan Pasukan untuk reset password.
          </p>
        </form>
      </div>
    </div>
  );
}
